/**
 * useUserSessions — TanStack Query hook over
 * `GET /organizations/:slug/users/:userId/sessions`.
 *
 * Status surface (closed):
 *   - 'loading'  — request in flight, or org slug / userId not ready.
 *   - 'ready'    — 200 OK; `data` populated with normalised rows.
 *   - 'forbidden' — 403; permission revoked or never granted.
 *   - 'notFound' — 404 (anti-enum: wrong-org, wrong-user, deleted user).
 *   - 'error'    — anything else.
 */

import { useQuery } from '@tanstack/react-query';
import { apiClient } from '../../../shared/api-client.js';
import { useCurrentOrg } from '../../../shared/hooks/useCurrentOrg.js';
import type { UserSessionRow } from './types.js';

const FORBIDDEN = Symbol('user-sessions:forbidden');
const NOT_FOUND = Symbol('user-sessions:notFound');

export type UseUserSessionsResult =
  | { readonly status: 'loading'; readonly refetch: () => void }
  | {
      readonly status: 'ready';
      readonly data: readonly UserSessionRow[];
      readonly refetch: () => void;
    }
  | { readonly status: 'forbidden'; readonly refetch: () => void }
  | { readonly status: 'notFound'; readonly refetch: () => void }
  | { readonly status: 'error'; readonly refetch: () => void };

interface SessionDto {
  readonly sid: string;
  readonly created_at: number;
  readonly last_seen_at: number;
}

interface SessionEnvelope {
  readonly data: readonly SessionDto[];
}

function projectSession(dto: SessionDto): UserSessionRow {
  return {
    sid: dto.sid,
    createdAt: dto.created_at,
    lastSeenAt: dto.last_seen_at,
  };
}

export function buildUserSessionsQueryKey(
  orgSlug: string | null,
  userId: string | undefined,
): readonly unknown[] {
  return ['identity', 'users', orgSlug, 'sessions', userId];
}

export function useUserSessions(
  userId: string | undefined,
): UseUserSessionsResult {
  const orgState = useCurrentOrg();
  const orgSlug = orgState?.membership.organizationSlug ?? null;
  const enabled =
    orgSlug !== null && typeof userId === 'string' && userId.length > 0;

  const query = useQuery({
    queryKey: buildUserSessionsQueryKey(orgSlug, userId),
    enabled,
    retry: false,
    queryFn: async () => {
      const result = await apiClient.GET(
        '/organizations/{slug}/users/{userId}/sessions',
        {
          params: {
            path: { slug: orgSlug as string, userId: userId as string },
          },
        },
      );
      if (result.response.status === 403) return FORBIDDEN;
      if (result.response.status === 404) return NOT_FOUND;
      if (!result.response.ok || result.data === undefined) {
        throw new Error(
          `GET /organizations/${orgSlug}/users/${userId}/sessions failed with status ${result.response.status}`,
        );
      }
      return (result.data as SessionEnvelope).data.map(projectSession);
    },
  });

  const refetch = (): void => {
    void query.refetch();
  };

  if (!enabled || query.isPending) return { status: 'loading', refetch };
  if (query.isError) return { status: 'error', refetch };
  if (query.data === FORBIDDEN) return { status: 'forbidden', refetch };
  if (query.data === NOT_FOUND) return { status: 'notFound', refetch };
  return { status: 'ready', data: query.data, refetch };
}
